import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import KycModal from "../components/KycModal";
import {
  ArrowRight, Calculator, Car, DollarSign, MapPin, Plus, Sparkles,
  TrendingUp, ShieldCheck, Building2, Wallet, LayoutGrid, FileText,
} from "lucide-react";
import HostPayoutForm from "../components/host/HostPayoutForm";
import HostStatements from "../components/host/HostStatements";
import FractionalizeModal from "../components/FractionalizeModal";
import TripInspectionPanel from "../components/TripInspectionPanel";
import TripMessagesPanel from "../components/TripMessagesPanel";
import InspectionReport from "../components/InspectionReport";
import ListingImage from "../components/ListingImage";
import ParkingMissingPhotosBanner from "../components/ParkingMissingPhotosBanner";
import ParkingSpotPhotoEditor from "../components/ParkingSpotPhotoEditor";
import { PARKING_FALLBACK } from "../lib/parkingImages";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";
import { pageItems } from "../lib/pageItems";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { bookingTimeZoneLabel, formatTripRange, formatTripDateLabel, formatTripTimeLabel } from "../lib/dateLocal";
import { formatUsd } from "../lib/currencyDisplay";
import { setReturnTo } from "../lib/returnTo";
import { HOST_CALCULATOR_CITIES } from "../lib/hostCalculatorCities";
import ParkingHostActions from "../components/ParkingHostActions";
import VehicleHostActions, { vehicleStatusBadgeClass } from "../components/VehicleHostActions";
import ReportIssueModal from "../components/ReportIssueModal";
import HostBookingDetails from "../components/HostBookingDetails";

const COMMISSION = 0.18;
const UTILIZATION = { conservative: 12, average: 18, premium: 24 };

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "cars", label: "Cars", icon: Car },
  { id: "parking", label: "Parking", icon: MapPin },
  { id: "statements", label: "Statements", icon: FileText },
  { id: "payouts", label: "Banking", icon: Building2 },
];

export default function HostHub() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const wantsDashboard = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.has("tab") || params.has("spot_id") || params.get("photos") === "1";
  }, [location.search]);
  const shouldRedirectLogin = !loading && !user && wantsDashboard;

  useEffect(() => {
    if (shouldRedirectLogin) {
      setReturnTo(location.pathname + location.search);
    }
  }, [shouldRedirectLogin, location.pathname, location.search]);

  if (shouldRedirectLogin) {
    return <Navigate to="/login" replace state={{ authRequired: true }} />;
  }
  if (!user) return <HostMarketing />;
  return <HostDashboard />;
}

function HostMarketing() {
  const [city, setCity] = useState("Toronto");
  const [vClass, setVClass] = useState("luxury");
  const [demand, setDemand] = useState("average");

  const calc = useMemo(() => {
    const ratesForCity = HOST_CALCULATOR_CITIES[city] || HOST_CALCULATOR_CITIES.Other;
    const dailyRate = ratesForCity[vClass];
    const utilDays = UTILIZATION[demand];
    const grossMonthly = dailyRate * utilDays;
    const fee = grossMonthly * COMMISSION;
    const netMonthly = grossMonthly - fee;
    return { dailyRate, utilDays, grossMonthly, fee, netMonthly, annual: netMonthly * 12 };
  }, [city, vClass, demand]);

  return (
    <div data-testid="host-marketing">
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden" style={{
          background: "radial-gradient(900px 520px at 85% 0%, rgba(225,29,72,0.09), transparent 55%)",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16">
          <div className="eyebrow mb-4">Host on Z-Cars</div>
          <h1 className="zc-headline text-4xl md:text-7xl tracking-tight max-w-3xl leading-[1.05]">
            Cars, parking &amp; payouts in <span className="italic" style={{ color: "var(--zc-accent)" }}>one hub</span>.
          </h1>
          <p className="text-[var(--zc-text-2)] mt-6 max-w-xl text-lg">
            List vehicles and parking spaces, track bookings, and add your bank details for weekly direct deposit payouts — all from a single host dashboard.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/login" className="inline-flex items-center gap-2 px-6 h-12 zc-btn-primary text-sm" data-testid="host-signin-cta">
              Sign in to host <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-14 max-w-3xl">
            <MiniPillar title="List cars" body="Curated fleet marketplace · 82% of trip revenue" />
            <MiniPillar title="List parking" body="Driveways, garages & lots · hourly or daily" />
            <MiniPillar title="Get paid" body="Add banking once · payouts for both verticals" />
          </div>
        </div>
      </section>

      <section id="calculator" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <HostCalculator city={city} setCity={setCity} vClass={vClass} setVClass={setVClass} demand={demand} setDemand={setDemand} calc={calc} signedOut />
      </section>
    </div>
  );
}

function HostDashboard() {
  const { user, checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const photoSpotId = params.get("spot_id");
  const showPhotoEditor = params.get("photos") === "1" && photoSpotId;
  const [kycOpen, setKycOpen] = useState(false);
  const kycOk = user?.kyc_status === "approved";

  const requireKycFor = () => (e) => {
    if (kycOk) return;
    e.preventDefault();
    setKycOpen(true);
    toast.error("Verify your identity before listing");
  };

  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [spots, setSpots] = useState([]);
  const [carBookings, setCarBookings] = useState([]);
  const [parkBookings, setParkBookings] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [offerings, setOfferings] = useState([]);
  const [payoutProfile, setPayoutProfile] = useState(null);
  const [payoutConfigured, setPayoutConfigured] = useState(false);
  const [fracTarget, setFracTarget] = useState(null);
  const [inspectBooking, setInspectBooking] = useState(null);
  const [returnReviewBooking, setReturnReviewBooking] = useState(null);
  const [returnReviewInspections, setReturnReviewInspections] = useState([]);
  const [returnReviewLoading, setReturnReviewLoading] = useState(false);
  const [returnReviewAcking, setReturnReviewAcking] = useState(false);
  const [amendRequests, setAmendRequests] = useState([]);
  const [amendBusy, setAmendBusy] = useState(null);
  const [amendNotes, setAmendNotes] = useState({});
  const [messageBooking, setMessageBooking] = useState(null);
  const [reportBooking, setReportBooking] = useState(null);
  const [parkReviewBooking, setParkReviewBooking] = useState(null);
  const [parkAmendCheckOut, setParkAmendCheckOut] = useState("");
  const [parkAmendBusy, setParkAmendBusy] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);
  const [payoutProfileForbidden, setPayoutProfileForbidden] = useState(false);

  const setTab = (id) => {
    const next = new URLSearchParams(params);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    next.delete("spot_id");
    next.delete("photos");
    next.delete("statement");
    setParams(next, { replace: true });
  };

  const closePhotoEditor = () => {
    const next = new URLSearchParams(params);
    next.delete("spot_id");
    next.delete("photos");
    setParams(next, { replace: true });
  };

  const onParkingPhotosSaved = (updated) => {
    setSpots((prev) => prev.map((s) => (s.spot_id === updated.spot_id ? updated : s)));
    closePhotoEditor();
    load();
  };

  const load = async () => {
    setLoading(true);
    setPayoutProfileForbidden(false);
    const sections = [
      { label: "summary", set: setSummary, pick: (r) => r.data },
      { label: "vehicles", set: setVehicles, pick: (r) => pageItems(r.data) },
      { label: "parking", set: setSpots, pick: (r) => pageItems(r.data) },
      { label: "car bookings", set: setCarBookings, pick: (r) => pageItems(r.data) },
      { label: "parking bookings", set: setParkBookings, pick: (r) => pageItems(r.data) },
      { label: "wallet", set: setWallet, pick: (r) => r.data },
      ...(FRACTIONAL_ENABLED
        ? [{ label: "offerings", set: setOfferings, pick: (r) => pageItems(r.data), fallback: [] }]
        : []),
      { label: "payout profile", set: setPayoutProfile, pick: (r) => r.data?.profile },
      { label: "amend requests", set: setAmendRequests, pick: (r) => pageItems(r.data), fallback: [] },
    ];
    const requests = [
      axios.get(`${API}/host/summary`, { withCredentials: true }),
      axios.get(`${API}/vehicles/mine`, { withCredentials: true }),
      axios.get(`${API}/parking/mine`, { withCredentials: true }),
      axios.get(`${API}/bookings/host`, { withCredentials: true }),
      axios.get(`${API}/parking-bookings/host`, { withCredentials: true }),
      axios.get(`${API}/wallet/me`, { withCredentials: true }),
      ...(FRACTIONAL_ENABLED
        ? [axios.get(`${API}/fractional/offerings/owner/me`, { withCredentials: true })]
        : []),
      axios.get(`${API}/host/payout-profile`, { withCredentials: true }),
      axios.get(`${API}/bookings/amend-requests/pending`, { withCredentials: true }),
    ];
    const results = await Promise.allSettled(requests);
    const failed = [];
    results.forEach((r, i) => {
      const sec = sections[i];
        if (r.status === "fulfilled") {
        sec.set(sec.pick(r.value));
        if (sec.label === "payout profile") setPayoutConfigured(!!r.value.data?.configured);
      } else {
        if ("fallback" in sec) sec.set(sec.fallback);
        failed.push(sec.label);
        if (sec.label === "payout profile" && r.reason?.response?.status === 403) {
          setPayoutProfileForbidden(true);
        }
      }
    });
    setLoadErrors(failed);
    if (failed.length === results.length && results[0]?.reason?.response?.status === 403) {
      toast.error("Switch to host or hybrid mode to use the host hub");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openReturnReview = async (b) => {
    setReturnReviewBooking(b);
    setReturnReviewInspections([]);
    setReturnReviewLoading(true);
    try {
      const r = await axios.get(`${API}/bookings/${b.booking_id}/inspections`, { withCredentials: true });
      setReturnReviewInspections(pageItems(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load return inspection");
      setReturnReviewBooking(null);
    } finally {
      setReturnReviewLoading(false);
    }
  };

  const ackReturnReview = async () => {
    if (!returnReviewBooking) return;
    setReturnReviewAcking(true);
    try {
      await axios.post(`${API}/bookings/${returnReviewBooking.booking_id}/ack-return-review`, {}, { withCredentials: true });
      toast.success("Return review acknowledged — you can complete the trip");
      setReturnReviewBooking(null);
      setReturnReviewInspections([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not acknowledge review");
    } finally {
      setReturnReviewAcking(false);
    }
  };

  const completeCar = async (id) => {
    try {
      const r = await axios.post(`${API}/bookings/${id}/complete`, {}, { withCredentials: true });
      const d = r.data || {};
      const parts = [];
      if (d.base_payout != null) parts.push(`$${formatUsd(d.base_payout)} base`);
      if (Number(d.mileage_overage_host) > 0) parts.push(`$${formatUsd(d.mileage_overage_host)} overage`);
      if (Number(d.fuel_mismatch_host) > 0) parts.push(`$${formatUsd(d.fuel_mismatch_host)} fuel`);
      if (Number(d.late_return_host) > 0) parts.push(`$${formatUsd(d.late_return_host)} late`);
      const detail = parts.length ? ` (${parts.join(" + ")})` : "";
      toast.success(`Trip completed · payout $${formatUsd(d.payout)}${detail}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "return_inspection_required") toast.error("Renter must submit return inspection before you can complete the trip");
      else if (d === "host_return_review_required") toast.error("Review and acknowledge the return inspection first");
      else toast.error(d || "Failed");
    }
  };

  const completePark = async (id) => {
    try {
      const r = await axios.post(`${API}/parking-bookings/${id}/complete`, {}, { withCredentials: true });
      const d = r.data || {};
      const ot = Number(d.parking_overtime_fee) > 0
        ? ` (incl. $${formatUsd(d.parking_overtime_fee)} overtime)`
        : "";
      toast.success(`Completed · payout $${formatUsd(d.payout)}${ot}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openParkReview = (b) => {
    if (!b) {
      setParkReviewBooking(null);
      setParkAmendCheckOut("");
      return;
    }
    setParkReviewBooking(b);
    setParkAmendCheckOut(b.check_out || "");
  };

  const submitParkAmend = async () => {
    if (!parkReviewBooking) return;
    const checkOut = (parkAmendCheckOut || "").trim();
    if (!checkOut) {
      toast.error("Choose a new check-out time");
      return;
    }
    setParkAmendBusy(true);
    try {
      const r = await axios.post(
        `${API}/parking-bookings/${parkReviewBooking.booking_id}/amend`,
        { check_out: checkOut },
        { withCredentials: true },
      );
      const delta = Number(r.data?.quote?.delta_total || 0);
      if (r.data?.stripe?.status === "payment_required") {
        toast.message("End time updated — renter must pay the price difference");
      } else if (delta > 0) {
        toast.success(`End time updated · +$${formatUsd(delta)} charged`);
      } else if (delta < 0) {
        toast.success(`End time updated · $${formatUsd(Math.abs(delta))} refunded`);
      } else {
        toast.success("End time updated");
      }
      setParkReviewBooking(null);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "slot_conflict") toast.error("That window conflicts with another booking");
      else if (d === "parking_amend_requires_confirmed") toast.error("Only confirmed bookings can be amended");
      else toast.error(d || "Could not update end time");
    } finally {
      setParkAmendBusy(false);
    }
  };

  const respondAmend = async (req, action) => {
    setAmendBusy(req.amend_id);
    const note = (amendNotes[req.amend_id] || "").trim();
    try {
      await axios.post(
        `${API}/bookings/${req.booking_id}/amend-request/${req.amend_id}/respond`,
        { action, note: note || undefined },
        { withCredentials: true },
      );
      toast.success(action === "approve" ? "Change approved" : "Change declined");
      setAmendNotes((prev) => {
        const next = { ...prev };
        delete next[req.amend_id];
        return next;
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setAmendBusy(null);
    }
  };

  const offeringFor = (vid) => offerings.find((o) => o.vehicle_id === vid);
  const canList = !!user && !user.is_admin;
  const walletSnap = summary?.wallet ?? wallet?.wallet ?? {};
  const fiat = Number(walletSnap.fiat_balance ?? 0);
  const escrow = Number(walletSnap.escrow_balance ?? wallet?.wallet?.escrow_balance ?? 0);
  const lifetime = Number(walletSnap.lifetime_earnings ?? wallet?.wallet?.lifetime_earnings ?? 0);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 min-w-0" data-testid="host-hub">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <div className="eyebrow mb-2">Host hub</div>
          <h1 className="zc-headline text-4xl md:text-5xl tracking-tight">
            Manage your <span className="italic" style={{ color: "var(--zc-accent)" }}>listings</span>
          </h1>
          <p className="text-[var(--zc-text-2)] mt-2 max-w-lg">
            Cars and parking in one place. Add banking details so we can send weekly payouts to your account.
          </p>
        </div>
        {loading && (
          <p className="text-sm text-[var(--zc-text-3)]">Loading…</p>
        )}
        {canList && (
          <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
            <Link to="/dashboard/owner/new" onClick={requireKycFor()} className="px-5 h-11 min-w-[9.5rem] zc-btn-primary text-sm inline-flex items-center justify-center gap-2 text-center" data-testid="host-add-car">
              <Plus size={14} className="shrink-0" /> <span>List a car</span>
            </Link>
            <Link to="/parking/new" onClick={requireKycFor()} className="px-5 h-11 min-w-[9.5rem] zc-btn-ghost text-sm inline-flex items-center justify-center gap-2 text-center border border-[var(--zc-border)]" data-testid="host-add-parking">
              <Plus size={14} className="shrink-0" /> <span>List parking</span>
            </Link>
          </div>
        )}
      </div>
      {loadErrors.length > 0 && (
        <div className="mt-4 p-3 rounded-lg border border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.08)] text-sm flex flex-wrap items-center justify-between gap-2">
          <span className="text-[var(--zc-text-2)]">
            {loadErrors.includes("payout profile") && payoutProfileForbidden
              ? "Banking details couldn't load — host access is required. If you already list cars or parking, click Retry to refresh your account."
              : `Some sections failed to load: ${loadErrors.join(", ")}.`}
          </span>
          <button type="button" onClick={() => { checkAuth(); load(); }} className="px-3 h-8 zc-btn-ghost text-xs font-semibold">
            Retry
          </button>
        </div>
      )}
      {!payoutConfigured && (
        <button
          type="button"
          onClick={() => setTab("payouts")}
          className="w-full mt-6 zc-card p-4 flex items-center justify-between gap-4 text-left hover:border-[var(--zc-accent)] transition-colors min-w-0"
          data-testid="host-payout-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-[var(--zc-accent-soft)] text-[var(--zc-accent)] shrink-0">
              <Building2 size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm">Add your banking details</div>
              <div className="text-xs text-[var(--zc-text-3)] mt-0.5 break-words">Required before your first payout from cars or parking.</div>
            </div>
          </div>
          <ArrowRight size={16} className="text-[var(--zc-accent)] shrink-0" />
        </button>
      )}

      <div className="flex gap-2 mt-8 border-b border-[var(--zc-border)] pb-px overflow-x-auto no-scrollbar flex-nowrap -mx-1 px-1" data-testid="host-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              "px-4 h-10 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors shrink-0 " +
              (tab === id
                ? "border-[var(--zc-accent)] text-[var(--zc-accent)]"
                : "border-transparent text-[var(--zc-text-3)] hover:text-[var(--zc-text)]")
            }
            data-testid={`host-tab-${id}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8 space-y-8" data-testid="host-tab-overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Available balance" value={`$${formatUsd(fiat)}`} sub={`Escrow $${formatUsd(escrow)}`} />
            <StatCard label="Car listings" value={summary?.vehicles_total ?? vehicles.length} sub={`${summary?.vehicles_live ?? 0} live`} />
            <StatCard label="Parking spots" value={summary?.parking_total ?? spots.length} sub={`${summary?.parking_live ?? 0} live`} />
            <StatCard label="Active bookings" value={(summary?.active_car_bookings || 0) + (summary?.active_parking_bookings || 0)} sub="Cars + parking" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="zc-card p-6">
              <div className="eyebrow mb-2">Cars</div>
              <p className="text-sm text-[var(--zc-text-2)]">
                {FRACTIONAL_ENABLED
                  ? "Fleet bookings, fractional offerings, and trip completion payouts."
                  : "Fleet bookings and trip completion payouts."}
              </p>
              <button type="button" onClick={() => setTab("cars")} className="mt-4 text-sm font-semibold text-[var(--zc-accent)] inline-flex items-center gap-1">
                Open fleet <ArrowRight size={14} />
              </button>
            </div>
            <div className="zc-card p-6">
              <div className="eyebrow mb-2">Parking</div>
              <p className="text-sm text-[var(--zc-text-2)]">Hourly spots, check-in windows, and host completion flow.</p>
              <button type="button" onClick={() => setTab("parking")} className="mt-4 text-sm font-semibold text-[var(--zc-accent)] inline-flex items-center gap-1">
                Open parking <ArrowRight size={14} />
              </button>
            </div>
          </div>
          <div className="zc-card p-6">
            <div className="flex items-center gap-2 eyebrow mb-4"><Wallet size={12} /> Payouts</div>
            {payoutConfigured ? (
              <p className="text-sm text-[var(--zc-text-2)]">
                Banking on file · {payoutProfile?.bank_name} ····{payoutProfile?.account_number_last4}
                <button type="button" onClick={() => setTab("payouts")} className="ml-2 text-[var(--zc-accent)] font-semibold">Edit</button>
              </p>
            ) : (
              <p className="text-sm text-[var(--zc-text-2)]">
                No bank account yet.
                <button type="button" onClick={() => setTab("payouts")} className="ml-2 text-[var(--zc-accent)] font-semibold">Add banking</button>
              </p>
            )}
          </div>
        </div>
      )}

      {tab === "cars" && (
        <div className="mt-8 space-y-10" data-testid="host-tab-cars">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="host-vehicles">
            {vehicles.map((v) => (
              <div key={v.vehicle_id} className="zc-card overflow-hidden">
                <div className="aspect-[16/10] bg-[var(--zc-bg-3)] relative">
                  <ListingImage images={v.images} alt="" className="w-full h-full object-cover" />
                  {!v.images?.length && (
                    <p
                      className="absolute bottom-0 left-0 right-0 text-white text-xs px-2 py-1"
                      style={{ background: "var(--zc-accent)" }}
                      data-testid={`host-veh-photo-missing-${v.vehicle_id}`}
                    >
                      Add photos to replace the placeholder
                    </p>
                  )}
                </div>
                <div className="p-4 min-w-0">
                  <div className="flex justify-between gap-2 min-w-0">
                    <div className="zc-title text-lg truncate min-w-0">{v.year} {v.make} {v.model}</div>
                    <StatusBadge status={v.status} />
                  </div>
                  <div className="text-xs text-[var(--zc-text-3)] mt-1 truncate">${v.daily_rate}/day · {v.location_city}</div>
                  {(v.registration_number || v.vin) && (
                    <div className="font-mono zc-meta mt-1.5 truncate" data-testid={`host-veh-reg-${v.vehicle_id}`}>
                      {v.registration_number && <span>Plate {v.registration_number}</span>}
                      {v.registration_number && v.vin && <span> · </span>}
                      {v.vin && <span>VIN ····{String(v.vin).slice(-6)}</span>}
                    </div>
                  )}
                  {v.status === "rejected" && v.rejection_reason && (
                    <p className="text-xs text-[var(--zc-danger,#dc2626)] mt-2 line-clamp-2" data-testid={`host-veh-reject-${v.vehicle_id}`}>
                      {v.rejection_reason}
                    </p>
                  )}
                  {v.baseline_stale && (
                    <p className="text-xs text-[var(--zc-warn,#f59e0b)] mt-2 font-semibold" data-testid={`host-veh-baseline-${v.vehicle_id}`}>
                      Fresh AI baseline scan required before resubmit
                    </p>
                  )}
                  {["pending", "approved", "paused", "rejected"].includes(v.status) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {v.status === "rejected" ? (
                        <Link
                          to={`/dashboard/owner/edit/${v.vehicle_id}`}
                          className="px-3 h-8 text-xs zc-btn-primary inline-flex items-center"
                          data-testid={`fix-resubmit-${v.vehicle_id}`}
                        >
                          Fix &amp; resubmit
                        </Link>
                      ) : (
                        <Link
                          to={`/dashboard/owner/edit/${v.vehicle_id}`}
                          className="px-3 h-8 text-xs zc-btn-ghost inline-flex items-center"
                          data-testid={`edit-listing-${v.vehicle_id}`}
                        >
                          Edit listing
                        </Link>
                      )}
                    </div>
                  )}
                  {FRACTIONAL_ENABLED && v.status === "approved" && !offeringFor(v.vehicle_id) && (
                    <button
                      type="button"
                      onClick={() => setFracTarget(v)}
                      className="mt-3 w-full h-9 text-xs flex items-center justify-center gap-1.5 rounded-md border font-semibold"
                      style={{ background: "rgba(37,99,235,0.06)", borderColor: "rgba(37,99,235,0.35)", color: "var(--zc-secondary, #2563eb)" }}
                    >
                      <Sparkles size={12} /> Fractionalize
                    </button>
                  )}
                  <VehicleHostActions vehicle={v} onUpdated={load} />
                </div>
              </div>
            ))}
            {vehicles.length === 0 && <EmptyCard text="No cars listed yet." cta="List a car" to="/dashboard/owner/new" onCtaClick={requireKycFor()} />}
          </div>
          {amendRequests.length > 0 && (
            <div className="zc-card overflow-hidden" data-testid="host-amend-requests">
              <div className="px-6 py-3 border-b border-[var(--zc-border)] eyebrow">Pending trip change requests</div>
              {amendRequests.map((req) => {
                const snap = req.booking_snapshot?.vehicle_snapshot || {};
                return (
                  <div key={req.amend_id} className="px-6 py-4 border-b border-[var(--zc-border)] last:border-0 text-sm">
                    <div className="font-semibold">{snap.year} {snap.make} {snap.model} · {req.booking_snapshot?.renter_name || "Renter"}</div>
                    <div className="text-xs text-[var(--zc-text-2)] mt-1">
                      New return: <b>{formatTripDateLabel(req.end_date)} · {formatTripTimeLabel(req.end_date)}</b> · Drop-off: {req.dropoff_location}
                      {req.dropoff_postal && <> · {req.dropoff_postal}</>}
                    </div>
                    {req.reason && <div className="text-xs text-[var(--zc-text-3)] mt-1">Reason: {req.reason}</div>}
                    <AmendFareSummary req={req} />
                    <label className="block mt-3">
                      <span className="text-xs text-[var(--zc-text-3)]">Note to renter (optional)</span>
                      <textarea
                        value={amendNotes[req.amend_id] || ""}
                        onChange={(e) => setAmendNotes((prev) => ({ ...prev, [req.amend_id]: e.target.value }))}
                        disabled={amendBusy === req.amend_id}
                        className="zc-input w-full p-2 h-16 mt-1 text-xs"
                        placeholder="Explain your decision…"
                        data-testid={`amend-note-${req.amend_id}`}
                      />
                    </label>
                    <div className="flex gap-2 mt-3">
                      <button type="button" disabled={amendBusy === req.amend_id} onClick={() => respondAmend(req, "approve")} className="px-3 h-8 zc-btn-primary text-xs" data-testid={`amend-approve-${req.amend_id}`}>Approve</button>
                      <button type="button" disabled={amendBusy === req.amend_id} onClick={() => respondAmend(req, "deny")} className="px-3 h-8 zc-btn-ghost text-xs" data-testid={`amend-deny-${req.amend_id}`}>Deny</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <BookingsTable
            title="Car bookings"
            rows={carBookings}
            empty="No car bookings yet."
            onRowClick={(b) => setDetailBooking(b)}
            renderRow={(b) => (
              <>
                <div data-label="Listing" className="font-semibold">
                  {b.vehicle_snapshot?.year} {b.vehicle_snapshot?.make} {b.vehicle_snapshot?.model}
                  {b.vehicle_id && (
                    <div className="text-[10px] font-mono text-[var(--zc-text-3)] mt-0.5">Vehicle ID: {b.vehicle_id}</div>
                  )}
                </div>
                <div data-label="Renter">
                  <div className="text-[var(--zc-text-2)]">{b.renter_display?.label || b.renter_name}</div>
                  {b.renter_display?.short_id && (
                    <div className="text-[10px] font-mono text-[var(--zc-text-3)] mt-0.5">ID {b.renter_display.short_id}</div>
                  )}
                  {b.booking_id && (
                    <div className="text-[10px] font-mono text-[var(--zc-text-3)] mt-0.5">Booking ID: {b.booking_id}</div>
                  )}
                </div>
                <div data-label="Dates" className="text-[var(--zc-text-2)] text-xs break-words">
                  <div>{formatTripRange(b.start_date, b.end_date) || "—"}</div>
                  {bookingTimeZoneLabel(b) && (
                    <div className="text-2xs text-[var(--zc-text-3)] mt-1">
                      {bookingTimeZoneLabel(b)}
                    </div>
                  )}
                </div>
                <div data-label="Status" className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className={"dot " + bookingStatusDotClass(b.status)} />
                    {formatBookingStatus(b)}
                  </div>
                  {b.status === "confirmed" && !b.pickup_inspection_id && (
                    <div className="zc-meta normal-case mt-0.5">Awaiting pickup scan</div>
                  )}
                  {b.status === "active" && !b.return_inspection_id && (
                    <div className="zc-hint text-[var(--zc-text-2)] normal-case mt-0.5">Awaiting return scan</div>
                  )}
                </div>
                <div data-label="Actions" className="zc-stack-actions text-center space-y-1">
                  {(b.status === "confirmed" || b.status === "active") && (
                    <button
                      type="button"
                      onClick={() => setMessageBooking(messageBooking?.booking_id === b.booking_id ? null : b)}
                      className="px-3 h-8 zc-btn-ghost text-xs inline-flex items-center gap-1 w-full justify-center"
                      data-testid={`host-msg-toggle-${b.booking_id}`}
                    >
                      {messageBooking?.booking_id === b.booking_id ? "Hide messages" : "Messages"}
                    </button>
                  )}
                  {b.status === "active" && !b.return_inspection_id && (
                    <button
                      type="button"
                      onClick={() => setInspectBooking(inspectBooking?.booking_id === b.booking_id ? null : b)}
                      className="px-3 h-8 zc-btn-ghost text-xs inline-flex items-center gap-1 w-full justify-center"
                    >
                      Scan return
                    </button>
                  )}
                  {b.status === "active" && b.return_inspection_id && !b.host_return_reviewed_at && (
                    <button type="button" onClick={() => openReturnReview(b)} className="px-3 h-8 zc-btn-primary text-xs inline-flex items-center gap-1 w-full justify-center">
                      Review return
                    </button>
                  )}
                  {b.status === "active" && b.return_inspection_id && b.host_return_reviewed_at && (
                    <button type="button" onClick={() => completeCar(b.booking_id)} className="px-3 h-8 zc-btn-primary text-xs inline-flex items-center gap-1 w-full justify-center">
                      <DollarSign size={12} /> Complete trip
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setReportBooking(b)}
                    className="px-3 h-8 zc-btn-ghost text-xs w-full justify-center"
                    data-testid={`host-report-issue-${b.booking_id}`}
                  >
                    Report issue
                  </button>
                </div>
              </>
            )}
            renderAfterRow={(b) => {
              const showMessages = messageBooking?.booking_id === b.booking_id && (b.status === "confirmed" || b.status === "active");
              const showInspect = inspectBooking?.booking_id === b.booking_id;
              const showReturnReview = returnReviewBooking?.booking_id === b.booking_id;
              if (!showMessages && !showInspect && !showReturnReview) return null;
              return (
                <div className="px-4 sm:px-6 pb-4 space-y-4">
                  {showMessages && <TripMessagesPanel bookingId={b.booking_id} compact />}
                  {showInspect && (
                    <div data-testid="host-return-inspection">
                      <TripInspectionPanel
                        booking={inspectBooking}
                        asHost
                        onDone={() => { setInspectBooking(null); load(); }}
                      />
                    </div>
                  )}
                  {showReturnReview && (
                    <div className="zc-card p-5" data-testid="host-return-review">
                      <div className="eyebrow mb-2">Return review</div>
                      <p className="text-sm text-[var(--zc-text-2)] mb-4">
                        Review the renter&apos;s return inspection before completing the trip and releasing payout.
                      </p>
                      {returnReviewLoading && <div className="text-sm text-[var(--zc-text-3)]">Loading inspection…</div>}
                      {!returnReviewLoading && returnReviewInspections.map((insp) => (
                        <InspectionReport key={insp.inspection_id} inspection={insp} />
                      ))}
                      {!returnReviewLoading && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          <button type="button" onClick={() => { setReturnReviewBooking(null); setReturnReviewInspections([]); }} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
                          <button type="button" onClick={ackReturnReview} disabled={returnReviewAcking || returnReviewLoading} className="px-4 h-10 zc-btn-primary text-sm">
                            {returnReviewAcking ? "Saving…" : "Acknowledge & continue"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }}
          />
        </div>
      )}

      {tab === "parking" && (
        <div className="mt-8 space-y-10" data-testid="host-tab-parking">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="host-parking-spots">
            {spots.map((s) => (
              <div key={s.spot_id} className="zc-card overflow-hidden">
                <div className="aspect-[16/10] bg-[var(--zc-bg-3)] overflow-hidden">
                  <ListingImage images={s.images} fallback={PARKING_FALLBACK} alt={s.name} className="w-full h-full object-cover" />
                </div>
                <ParkingMissingPhotosBanner spot={s} />
                <div className="p-4">
                  <div className="flex justify-between gap-2">
                    <div className="zc-title text-lg line-clamp-1">{s.name}</div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xs text-[var(--zc-text-3)] mt-1 flex items-center gap-1">
                    <MapPin size={11} /> {s.city} · ${formatUsd(s.price_per_hour)}/hr
                    {s.max_hours ? ` · max ${s.max_hours}h` : ""}
                  </div>
                  {s.status === "rejected" && s.rejection_reason && (
                    <p className="text-xs text-[var(--zc-danger,#dc2626)] mt-2 line-clamp-2">{s.rejection_reason}</p>
                  )}
                  {["pending", "approved", "paused", "rejected"].includes(s.status) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to={`/parking/edit/${s.spot_id}`}
                        className={"px-3 h-8 text-xs inline-flex items-center " + (s.status === "rejected" ? "zc-btn-primary" : "zc-btn-ghost")}
                        data-testid={`edit-parking-${s.spot_id}`}
                      >
                        {s.status === "rejected" ? "Fix & resubmit" : "Edit listing"}
                      </Link>
                    </div>
                  )}
                  <ParkingHostActions spot={s} onUpdated={load} />
                </div>
                {showPhotoEditor && photoSpotId === s.spot_id && (
                  <ParkingSpotPhotoEditor spot={s} onSaved={onParkingPhotosSaved} onClose={closePhotoEditor} />
                )}
              </div>
            ))}
            {spots.length === 0 && <EmptyCard text="No parking listed yet." cta="List parking" to="/parking/new" onCtaClick={requireKycFor()} />}
          </div>
          <BookingsTable
            title="Parking bookings"
            rows={parkBookings}
            empty="No parking bookings yet."
            cols={5}
            renderRow={(b) => (
              <>
                <div data-label="Listing" className="font-semibold line-clamp-1">{b.spot_snapshot?.name}</div>
                <div data-label="Renter" className="text-[var(--zc-text-2)]">{b.renter_name}</div>
                <div data-label="Dates" className="text-[var(--zc-text-2)] text-xs break-words">{formatTripRange(b.check_in, b.check_out) || "—"}</div>
                <div data-label="Status" className="flex items-center gap-2">
                  <span className={"dot " + bookingStatusDotClass(b.status)} />
                  {formatBookingStatus(b)}
                </div>
                <div data-label="Actions" className="zc-stack-actions text-center space-y-1">
                  {b.status === "confirmed" && (
                    <button
                      type="button"
                      onClick={() => openParkReview(parkReviewBooking?.booking_id === b.booking_id ? null : b)}
                      className="px-3 h-8 zc-btn-ghost text-xs inline-flex items-center gap-1 w-full justify-center"
                      data-testid={`host-park-review-${b.booking_id}`}
                    >
                      {parkReviewBooking?.booking_id === b.booking_id ? "Hide review" : "Review"}
                    </button>
                  )}
                  {b.status === "confirmed" && (
                    <button
                      type="button"
                      onClick={() => completePark(b.booking_id)}
                      className="px-3 h-8 zc-btn-primary text-xs inline-flex items-center gap-1 w-full justify-center"
                    >
                      <DollarSign size={12} /> Complete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setReportBooking(b)}
                    className="px-3 h-8 zc-btn-ghost text-xs w-full justify-center"
                    data-testid={`host-park-report-${b.booking_id}`}
                  >
                    Report issue
                  </button>
                </div>
              </>
            )}
            renderAfterRow={(b) => {
              if (parkReviewBooking?.booking_id !== b.booking_id) return null;
              return (
                <div className="px-4 sm:px-6 pb-4" data-testid="host-park-review-panel">
                  <div className="zc-card p-5">
                    <div className="eyebrow mb-2">Review parking booking</div>
                    <p className="text-sm text-[var(--zc-text-2)] mb-4">
                      Amend the check-out time if needed. Price updates apply immediately (renter pays any increase; refunds return to their original payment method when possible).
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4 text-sm mb-4">
                      <div className="text-left">
                        <div className="eyebrow mb-1">Check-in</div>
                        <div className="font-semibold">{formatTripDateLabel(b.check_in)}</div>
                        <div className="text-[var(--zc-text-2)] text-xs mt-0.5">{formatTripTimeLabel(b.check_in)}</div>
                      </div>
                      <div className="text-right">
                        <div className="eyebrow mb-1">Current check-out</div>
                        <div className="font-semibold">{formatTripDateLabel(b.check_out)}</div>
                        <div className="text-[var(--zc-text-2)] text-xs mt-0.5">{formatTripTimeLabel(b.check_out)}</div>
                      </div>
                    </div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold mb-1">
                      New check-out
                    </label>
                    <input
                      type="datetime-local"
                      value={parkAmendCheckOut}
                      onChange={(e) => setParkAmendCheckOut(e.target.value)}
                      className="w-full max-w-sm h-10 px-3 rounded-lg border border-[var(--zc-border)] bg-white text-sm"
                      data-testid="host-park-amend-checkout"
                    />
                    <div className="flex flex-wrap gap-2 mt-4 justify-center">
                      <button type="button" onClick={() => setParkReviewBooking(null)} className="px-4 h-10 zc-btn-ghost text-sm">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={submitParkAmend}
                        disabled={parkAmendBusy}
                        className="px-4 h-10 zc-btn-primary text-sm"
                        data-testid="host-park-amend-save"
                      >
                        {parkAmendBusy ? "Saving…" : "Save end time"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}

      {tab === "payouts" && (
        <div className="mt-8 max-w-2xl" data-testid="host-tab-payouts">
          <div className="zc-card p-6 md:p-8">
            <div className="eyebrow mb-2">Banking & payouts</div>
            <h2 className="zc-headline text-2xl">Where should we send your earnings?</h2>
            <p className="text-sm text-[var(--zc-text-2)] mt-2 mb-6">
              One account covers payouts from car rentals and parking. We transfer available balance after trip completion (cars: net of 18% platform fee).
            </p>
            <HostPayoutForm
              profile={payoutProfile}
              configured={payoutConfigured}
              onSaved={(p) => {
                setPayoutProfile(p);
                setPayoutConfigured(true);
                load();
              }}
            />
          </div>
          <div className="zc-card p-5 mt-6 text-sm text-[var(--zc-text-2)]">
            <div className="font-semibold text-[var(--zc-text)] mb-2">Wallet snapshot</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><div className="eyebrow mb-1">Available</div><div className="zc-price text-lg sm:text-xl">${formatUsd(fiat)}</div></div>
              <div><div className="eyebrow mb-1">In escrow</div><div className="zc-price text-lg sm:text-xl">${formatUsd(escrow)}</div></div>
              <div><div className="eyebrow mb-1">Lifetime</div><div className="zc-price text-lg sm:text-xl">${formatUsd(lifetime)}</div></div>
            </div>
          </div>
        </div>
      )}

      {tab === "statements" && (
        <div className="mt-8" data-testid="host-tab-statements">
          <HostStatements requestedStatementId={params.get("statement")} />
        </div>
      )}

      {FRACTIONAL_ENABLED && (
      <FractionalizeModal open={!!fracTarget} vehicle={fracTarget} onClose={() => setFracTarget(null)} onCreated={load} />
      )}
      <KycModal
        open={kycOpen}
        onClose={() => setKycOpen(false)}
        onApproved={async () => { await checkAuth(); setKycOpen(false); }}
      />
      <ReportIssueModal
        booking={reportBooking}
        open={!!reportBooking}
        onClose={() => setReportBooking(null)}
      />
      <HostBookingDetails
        booking={detailBooking}
        open={!!detailBooking}
        onClose={() => setDetailBooking(null)}
      />
    </div>
  );
}

function AmendFareSummary({ req }) {
  const quote = req.requested_quote;
  const oldTotal = req.old_total ?? quote?.previous_total;
  const newTotal = req.new_total ?? quote?.total;
  const delta = req.price_delta ?? (
    oldTotal != null && newTotal != null ? Number(newTotal) - Number(oldTotal) : null
  );
  if (oldTotal == null && newTotal == null && delta == null) return null;

  const deltaNum = delta != null ? Number(delta) : null;
  const deltaLabel = deltaNum == null
    ? "—"
    : deltaNum === 0
      ? "$0.00"
      : `${deltaNum > 0 ? "+" : "−"}$${formatUsd(Math.abs(deltaNum))}`;

  return (
    <div
      className="mt-2 p-3 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)] text-xs space-y-1"
      data-testid={`amend-fare-${req.amend_id}`}
    >
      <div className="eyebrow mb-1">Fare change</div>
      {oldTotal != null && (
        <div className="flex justify-between gap-3">
          <span className="text-[var(--zc-text-3)]">Current total</span>
          <span className="font-semibold">${formatUsd(oldTotal)}</span>
        </div>
      )}
      {newTotal != null && (
        <div className="flex justify-between gap-3">
          <span className="text-[var(--zc-text-3)]">New total</span>
          <span className="font-semibold">${formatUsd(newTotal)}</span>
        </div>
      )}
      {deltaNum != null && (
        <div className="flex justify-between gap-3 pt-1 border-t border-[var(--zc-border)]">
          <span className="text-[var(--zc-text-3)]">Change</span>
          <span
            className="font-semibold"
            style={{ color: deltaNum > 0 ? "var(--zc-accent)" : deltaNum < 0 ? "var(--zc-success)" : "var(--zc-text)" }}
          >
            {deltaLabel}
          </span>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="zc-card p-5 min-w-0">
      <div className="eyebrow truncate">{label}</div>
      <div className="zc-stat text-2xl sm:text-3xl mt-2 truncate">{value}</div>
      {sub && <div className="text-xs text-[var(--zc-text-3)] mt-1 break-words">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = vehicleStatusBadgeClass(status);
  const label = status === "offboarded" ? "removed" : status;
  return <span className={`zc-badge font-semibold capitalize ${cls}`}>{label}</span>;
}

function EmptyCard({ text, cta, to, onCtaClick }) {
  return (
    <div className="zc-card p-8 col-span-full text-center">
      <p className="text-[var(--zc-text-3)] text-sm">{text}</p>
      <Link to={to} onClick={onCtaClick} className="inline-flex items-center justify-center mt-4 px-4 h-10 zc-btn-primary text-sm">{cta}</Link>
    </div>
  );
}

function BookingsTable({ title, rows, empty, renderRow, renderAfterRow, onRowClick, colClass = "md:grid-cols-5" }) {
  return (
    <div>
      <div className="eyebrow mb-3">{title}</div>
      <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table">
        <div className={`hidden md:grid ${colClass} zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold`}>
          <div>Listing</div><div>Renter</div><div>Dates</div><div>Status</div><div />
        </div>
        {rows.length === 0 && <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">{empty}</div>}
        {rows.map((b) => (
          <div key={b.booking_id} className="border-b border-[var(--zc-border)] last:border-0">
            <div
              className={`zc-stack-row md:grid ${colClass} md:items-center px-4 sm:px-6 py-4 text-sm gap-2${onRowClick ? " cursor-pointer hover:bg-[var(--zc-bg-3)] transition-colors" : ""}`}
              onClick={onRowClick ? (e) => {
                if (e.target.closest("button, a")) return;
                onRowClick(b);
              } : undefined}
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(b);
                }
              } : undefined}
            >
              {renderRow(b)}
            </div>
            {renderAfterRow?.(b)}
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniPillar({ title, body }) {
  return (
    <div className="zc-card p-5">
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-[var(--zc-text-2)] mt-1">{body}</div>
    </div>
  );
}

function HostCalculator({ city, setCity, vClass, setVClass, demand, setDemand, calc, signedOut }) {
  return (
    <div className="grid md:grid-cols-[1fr,1.2fr] gap-8">
      <div className="zc-card p-7">
        <div className="flex items-center gap-2 mb-4"><Calculator size={16} className="text-[var(--zc-accent)]" /><div className="eyebrow">Earnings calculator</div></div>
        <h2 className="zc-headline text-3xl">Estimate car hosting income</h2>
        <div className="mt-6 space-y-4">
          <select className="zc-input h-11 px-3 w-full text-sm" value={city} onChange={(e) => setCity(e.target.value)}>
            {Object.keys(HOST_CALCULATOR_CITIES).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            {["economy", "sedan", "suv", "electric", "luxury", "sports"].map((val) => (
              <button key={val} type="button" onClick={() => setVClass(val)} className={"px-3 h-9 text-xs zc-chip " + (vClass === val ? "zc-chip-active" : "")}>{val}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="zc-card p-7 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #fff 0%, var(--zc-accent-soft) 100%)" }}>
        <div className="zc-price text-4xl sm:text-5xl md:text-6xl">${Math.round(calc.netMonthly).toLocaleString()}</div>
        <div className="text-sm text-[var(--zc-text-2)] mt-2">projected monthly net (cars)</div>
        <Link
          to={signedOut ? "/login" : "/dashboard/owner/new"}
          className="w-full mt-8 h-12 zc-btn-primary text-sm flex items-center justify-center gap-2"
        >
          {signedOut ? "Sign in to list" : "List a car"} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
